/**
 * Authentication Store
 *
 * Replaces Alpine.store('app') for authentication-related state.
 * Manages user sign-in status, email, invitation code attempts, and password reset flow.
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'
import { authService } from '@/services/auth.service'
import { usePaymentStore } from './payment'
import { useSessionStore } from './session'

export const useAuthStore = defineStore('auth', () => {
  // ==================== STATE ====================

  /**
   * Is user currently signed in?
   * Replaces: Alpine.store('app').isSignedIn
   */
  const isSignedIn = ref(false)

  /**
   * Current user's email address
   * Replaces: Alpine.store('app').userEmail
   */
  const userEmail = ref('')

  /**
   * Does user have a valid invitation code?
   * Replaces: Alpine.store('app').hasInvitationCode
   */
  const hasInvitationCode = ref(false)

  /**
   * Number of failed invitation code attempts
   * After 3 attempts, user can sign up with address/zip instead
   * Replaces: Alpine.store('app').invitationAttempts
   */
  const invitationAttempts = ref(0)

  /**
   * Password reset token (from email link query parameter)
   * Replaces: Alpine.store('app').resetToken
   */
  const resetToken = ref('')

  /**
   * Email address for password reset (stored after request)
   * Used to display on confirmation page
   */
  const resetEmail = ref('')

  // ==================== ACTIONS ====================

  /**
   * Validate Session - Check if user is currently logged in
   * Replaces: Alpine.store('app').checkSession()
   *
   * Called on app initialization and after certain actions.
   */
  async function validateSession() {
    try {
      const response = await authService.getSessionStatus()

      const wasSignedIn = isSignedIn.value
      isSignedIn.value = response.data.is_signed_in
      userEmail.value = response.data.email || ''

      // Sync payment store
      const paymentStore = usePaymentStore()
      paymentStore.hasPurchased = response.data.has_purchased_pack || false

      // Initialize session monitoring if user is signed in and wasn't before
      if (isSignedIn.value && !wasSignedIn) {
        const sessionStore = useSessionStore()
        sessionStore.initializeSessionMonitoring()
        console.log('[AUTH] Session monitoring initialized after session validation')
      }

      return response.data
    } catch (error) {
      console.error('Session validation failed:', error)
      isSignedIn.value = false
      userEmail.value = ''
      throw error
    }
  }

  /**
   * Sign Up - Create new user account
   *
   * @param {Object} formData - Sign up form data
   * @returns {Promise} - Response data
   */
  async function signUp(formData) {
    try {
      const response = await authService.signUp(formData)

      // Update state after successful sign up
      isSignedIn.value = true
      userEmail.value = response.data.email

      // Sync payment store (new users haven't purchased yet)
      const paymentStore = usePaymentStore()
      paymentStore.hasPurchased = false

      // Initialize session monitoring
      const sessionStore = useSessionStore()
      sessionStore.initializeSessionMonitoring()
      console.log('[AUTH] Session monitoring initialized after sign up')

      return response.data
    } catch (error) {
      console.error('Sign up failed:', error)
      throw error
    }
  }

  /**
   * Sign In - Authenticate user
   *
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} - Response data
   */
  async function signIn(email, password) {
    try {
      const response = await authService.signIn(email, password)

      // Update state after successful sign in
      isSignedIn.value = true
      userEmail.value = response.data.email

      // Sync payment store
      const paymentStore = usePaymentStore()
      paymentStore.hasPurchased = response.data.has_purchased || false

      // Initialize session monitoring
      const sessionStore = useSessionStore()
      sessionStore.initializeSessionMonitoring()
      console.log('[AUTH] Session monitoring initialized after sign in')

      return response.data
    } catch (error) {
      console.error('Sign in failed:', error)
      throw error
    }
  }

  /**
   * Sign Out - End user session
   * Replaces: Alpine.store('app').signOut()
   */
  async function signOut() {
    try {
      await authService.signOut()

      // Clear state
      isSignedIn.value = false
      userEmail.value = ''
      hasInvitationCode.value = false
      invitationAttempts.value = 0
      resetToken.value = ''

      // Clear payment store
      const paymentStore = usePaymentStore()
      paymentStore.hasPurchased = false

      // Clean up session monitoring
      const sessionStore = useSessionStore()
      sessionStore.cleanup()
      console.log('[AUTH] Session monitoring cleaned up after sign out')

    } catch (error) {
      console.error('Sign out failed:', error)
      // Clear state anyway
      isSignedIn.value = false
      userEmail.value = ''

      // Clear payment store
      const paymentStore = usePaymentStore()
      paymentStore.hasPurchased = false

      // Clean up session monitoring
      const sessionStore = useSessionStore()
      sessionStore.cleanup()
    }
  }

  /**
   * Request Password Reset - Send reset email
   *
   * @param {string} email - User email
   * @returns {Promise} - Response data
   */
  async function requestPasswordReset(email) {
    try {
      const response = await authService.requestPasswordReset(email)
      return response.data
    } catch (error) {
      console.error('Password reset request failed:', error)
      throw error
    }
  }

  /**
   * Reset Password - Set new password using token
   *
   * @param {string} token - Reset token from email
   * @param {string} newPassword - New password
   * @returns {Promise} - Response data
   */
  async function resetPassword(token, newPassword) {
    try {
      const response = await authService.resetPassword(token, newPassword)

      // User is automatically signed in after password reset
      isSignedIn.value = true
      userEmail.value = response.data.user.email

      // Initialize session monitoring
      const sessionStore = useSessionStore()
      sessionStore.initializeSessionMonitoring()
      console.log('[AUTH] Session monitoring initialized after password reset')

      return response.data
    } catch (error) {
      console.error('Password reset failed:', error)
      throw error
    }
  }

  /**
   * Validate Invitation Code
   *
   * @param {string} code - Invitation code
   * @returns {Promise} - Validation result
   */
  async function validateInvitationCode(code) {
    try {
      const response = await authService.validateInvitation(code)

      if (response.data.valid) {
        hasInvitationCode.value = true
      } else {
        invitationAttempts.value++
      }

      return response.data
    } catch (error) {
      console.error('Invitation validation failed:', error)
      invitationAttempts.value++
      throw error
    }
  }

  /**
   * Refresh Session - Extend session timeout
   * Called when user clicks "Keep Working" in timeout warning
   */
  async function refreshSession() {
    try {
      await authService.refreshSession()
    } catch (error) {
      console.error('Session refresh failed:', error)
      throw error
    }
  }

  // ==================== RETURN (PUBLIC API) ====================

  return {
    // State
    isSignedIn,
    userEmail,
    hasInvitationCode,
    invitationAttempts,
    resetToken,
    resetEmail,

    // Actions
    validateSession,
    signUp,
    signIn,
    signOut,
    requestPasswordReset,
    resetPassword,
    validateInvitationCode,
    refreshSession
  }
})
